import i18next from 'i18next';
import { TokenUsageResponse } from '../../services/admin';

interface TokenUsageCardProps {
  tokenUsage: TokenUsageResponse | null;
  isLoading?: boolean;
}

function formatNumber(num: number): string {
  const locale = i18next.language === 'pl' ? 'pl-PL' : 'en-US';
  return num.toLocaleString(locale);
}

function formatCostPln(cost: number): string {
  return `${cost.toFixed(2)} zł`;
}

function formatCostUsd(cost: number): string {
  return `$${cost.toFixed(4)}`;
}

export function TokenUsageCard({ tokenUsage, isLoading }: TokenUsageCardProps) {
  if (isLoading) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mt-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          <span className="text-sm text-slate-500">Ładowanie danych o tokenach...</span>
        </div>
      </div>
    );
  }

  if (!tokenUsage) {
    return null;
  }

  // Check if we have actual data
  if (tokenUsage.message && tokenUsage.tokensTotal === 0) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mt-4">
        <h4 className="font-medium text-slate-900 dark:text-white mb-2">Użycie tokenów i koszty</h4>
        <p className="text-sm text-slate-500 dark:text-slate-400">{tokenUsage.message}</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 mt-4">
      <h4 className="font-medium text-slate-900 dark:text-white mb-3">Użycie tokenów i koszty</h4>

      {/* Model info */}
      <div className="flex items-center justify-between py-2 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <div className="w-1 h-6 bg-blue-500 rounded" />
          <span className="font-medium text-slate-900 dark:text-white">
            {tokenUsage.modelId || 'Nieznany model'}
          </span>
        </div>
        <span className="font-medium text-green-600 dark:text-green-400">
          {formatCostPln(tokenUsage.costPln.total)}
        </span>
      </div>

      {/* Token breakdown */}
      <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
        <div>
          <span className="text-slate-500 dark:text-slate-400 block">Input</span>
          <span className="font-medium text-slate-900 dark:text-white">
            {formatNumber(tokenUsage.tokensInput)}
          </span>
          <span className="text-xs text-slate-400 dark:text-slate-500 block">
            {formatCostPln(tokenUsage.costPln.input)}
          </span>
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400 block">Output</span>
          <span className="font-medium text-slate-900 dark:text-white">
            {formatNumber(tokenUsage.tokensOutput)}
          </span>
          <span className="text-xs text-slate-400 dark:text-slate-500 block">
            {formatCostPln(tokenUsage.costPln.output)}
          </span>
        </div>
        <div>
          <span className="text-slate-500 dark:text-slate-400 block">Turny</span>
          <span className="font-medium text-slate-900 dark:text-white">
            {tokenUsage.totalTurns}
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
        <span className="text-sm text-slate-500 dark:text-slate-400">Calkowity koszt:</span>
        <div className="text-right">
          <span className="font-semibold text-lg text-green-600 dark:text-green-400 block">
            {formatCostPln(tokenUsage.costPln.total)}
          </span>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            ({formatCostUsd(tokenUsage.costUsd.total)} • kurs: {tokenUsage.exchangeRate.toFixed(2)} PLN/USD)
          </span>
        </div>
      </div>
    </div>
  );
}

export default TokenUsageCard;
